import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Loader2, User, ExternalLink, Calendar, Briefcase, Building, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const ROLE_CHOICES = [
  { v: "organizer", l: "Organisateur", desc: "Publier vos marchés, salons, foires et festivals.", icon: Calendar },
  { v: "exhibitor", l: "Exposant", desc: "Réserver des stands et planifier votre saison.", icon: Briefcase },
  { v: "venue_owner", l: "Propriétaire de lieu", desc: "Louer votre lieu événementiel.", icon: Building },
  { v: "provider", l: "Prestataire", desc: "Proposer vos services (DJ, sécurité, food truck...).", icon: Sparkles },
];

export default function ProfileSettings() {
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", bio: "", picture: "", website: "", company: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [upgrading, setUpgrading] = useState("");

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        bio: user.bio || "",
        picture: user.picture || "",
        website: user.website || "",
        company: user.company || "",
      });
    }
  }, [user]);

  const onPicture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setForm((f) => ({ ...f, picture: url }));
      toast.success("Photo téléversée");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur upload");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/me/profile", form);
      toast.success("Profil enregistré");
      if (refreshUser) await refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const upgradeRole = async (newRole) => {
    const role = ROLE_CHOICES.find((r) => r.v === newRole);
    if (!window.confirm(`Confirmer le passage au rôle « ${role.l} » ?\n\nCe changement débloque immédiatement les fonctionnalités du tableau de bord ${role.l.toLowerCase()}.`)) return;
    setUpgrading(newRole);
    try {
      await api.post("/me/upgrade-role", { role: newRole });
      toast.success(`Bienvenue, ${role.l} !`);
      if (refreshUser) await refreshUser();
      setTimeout(() => nav("/dashboard"), 600);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la mise à niveau");
    } finally {
      setUpgrading("");
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <User className="h-5 w-5 text-accent" />
            <p className="text-label text-muted-foreground">Mon compte</p>
          </div>
          <h1 className="font-heading font-light tracking-editorial text-4xl text-ink">
            Modifier <em className="font-display text-accent">mon profil</em>
          </h1>
        </div>
        <Link
          to={`/users/${user.user_id}`}
          data-testid="view-public-profile"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-accent hover:underline"
        >
          <ExternalLink className="h-3 w-3" /> Voir mon profil public
        </Link>
      </div>

      <div className="bg-white border border-ink/10 rounded-md p-6 lg:p-8 space-y-6">
        {/* Avatar */}
        <div className="flex items-center gap-6">
          <Avatar className="h-20 w-20 ring-4 ring-cream-100">
            <AvatarImage src={form.picture || ""} />
            <AvatarFallback className="bg-ink text-cream text-xl font-display">
              {form.name?.[0]?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div>
            <Label htmlFor="picture-upload" className="cursor-pointer inline-block">
              <span className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold uppercase tracking-wider bg-cream-100 hover:bg-cream-200 text-ink rounded-none">
                {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : "Changer la photo"}
              </span>
              <input
                id="picture-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onPicture}
                disabled={uploading}
                data-testid="picture-upload"
              />
            </Label>
            <p className="text-xs text-muted-foreground mt-2">JPG ou PNG, 5 Mo max.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="name">Nom complet</Label>
            <Input
              id="name"
              data-testid="profile-input-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="company">Société / Marque</Label>
            <Input
              id="company"
              data-testid="profile-input-company"
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              placeholder="ex. Maison Dubois"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="website">Site web</Label>
          <Input
            id="website"
            data-testid="profile-input-website"
            value={form.website}
            onChange={(e) => setForm({ ...form, website: e.target.value })}
            placeholder="https://..."
          />
        </div>

        <div>
          <Label htmlFor="bio">Bio</Label>
          <Textarea
            id="bio"
            data-testid="profile-input-bio"
            value={form.bio}
            onChange={(e) => setForm({ ...form, bio: e.target.value })}
            placeholder="Présentez votre activité en quelques lignes. Cette bio est visible publiquement."
            rows={4}
          />
          <p className="text-xs text-muted-foreground mt-1">{form.bio?.length || 0} / 500</p>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-ink/10">
          <Button
            onClick={save}
            disabled={saving}
            data-testid="profile-save"
            className="bg-ink hover:bg-ink/85 text-cream rounded-none px-6"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
          </Button>
        </div>
      </div>

      {/* Role upgrade — visible only for visitors */}
      {user.role === "visitor" && (
        <div className="mt-10" data-testid="role-upgrade-section">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="h-5 w-5 text-accent" />
            <p className="text-label text-muted-foreground">Faire évoluer mon compte</p>
          </div>
          <h2 className="font-heading font-light tracking-editorial text-3xl text-ink mb-2">
            Devenir <em className="font-display text-accent">producteur</em>
          </h2>
          <p className="text-muted-foreground text-sm mb-6 max-w-2xl">
            Vous êtes actuellement inscrit comme <strong>visiteur</strong>. Activez l&apos;un des rôles ci-dessous pour publier vos événements, lieux ou services et débloquer le tableau de bord dédié.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {ROLE_CHOICES.map(({ v, l, desc, icon: Icon }) => (
              <button
                key={v}
                onClick={() => upgradeRole(v)}
                disabled={!!upgrading}
                data-testid={`upgrade-role-${v}`}
                className="bg-white border border-ink/10 rounded-md p-5 text-left hover:border-accent/40 hover:shadow-luxe-md transition group disabled:opacity-50"
              >
                <div className="flex items-start gap-3">
                  <div className="bg-cream-100 p-2.5 rounded-md group-hover:bg-accent/10 transition-colors">
                    <Icon className="h-4 w-4 text-ink/70 group-hover:text-accent transition-colors" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-medium text-ink mb-1">{l}</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">{desc}</p>
                  </div>
                  {upgrading === v ? (
                    <Loader2 className="h-4 w-4 animate-spin text-accent" />
                  ) : (
                    <ArrowRight className="h-4 w-4 text-ink/30 group-hover:text-accent transition-colors" />
                  )}
                </div>
              </button>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground mt-4 italic">
            Le changement de rôle est immédiat et conserve toutes vos données. Vous ne pourrez pas revenir en arrière depuis ce panneau (contactez le support si besoin).
          </p>
        </div>
      )}
    </div>
  );
}
